class AuthenticatorCrypto {
  private static readonly PBKDF2_ITERATIONS = 100000
  private static readonly SALT_LENGTH = 16
  private static readonly IV_LENGTH = 12

  async encryptSecret(secret: string, password: string): Promise<string> {
    try {
      const encoder = new TextEncoder()
      const passwordKey = await crypto.subtle.importKey(
        'raw',
        encoder.encode(password),
        { name: 'PBKDF2' },
        false,
        ['deriveBits', 'deriveKey']
      )

      const salt = crypto.getRandomValues(
        new Uint8Array(AuthenticatorCrypto.SALT_LENGTH)
      )
      const key = await this.deriveKey(passwordKey, salt)

      const iv = crypto.getRandomValues(
        new Uint8Array(AuthenticatorCrypto.IV_LENGTH)
      )
      const encrypted = await crypto.subtle.encrypt(
        {
          name: 'AES-GCM',
          iv: iv
        },
        key,
        encoder.encode(secret)
      )

      return this.combineEncryptionData(salt, iv, encrypted)
    } catch (error) {
      throw new Error(
        `Encryption failed: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  async decryptSecret(
    encryptedData: string,
    password: string
  ): Promise<string> {
    try {
      const { salt, iv, ciphertext } = this.extractEncryptionData(encryptedData)

      const encoder = new TextEncoder()
      const decoder = new TextDecoder()

      const passwordKey = await crypto.subtle.importKey(
        'raw',
        encoder.encode(password),
        { name: 'PBKDF2' },
        false,
        ['deriveBits', 'deriveKey']
      )

      const key = await this.deriveKey(passwordKey, salt)

      const decrypted = await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: iv as BufferSource
        },
        key,
        ciphertext as BufferSource
      )

      return decoder.decode(decrypted)
    } catch (error) {
      throw new Error(
        `Decryption failed: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  private async deriveKey(
    passwordKey: CryptoKey,
    salt: Uint8Array
  ): Promise<CryptoKey> {
    return await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt as BufferSource,
        iterations: AuthenticatorCrypto.PBKDF2_ITERATIONS,
        hash: 'SHA-256'
      },
      passwordKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    )
  }

  private combineEncryptionData(
    salt: Uint8Array,
    iv: Uint8Array,
    ciphertext: ArrayBuffer
  ): string {
    const combined = new Uint8Array(
      salt.length + iv.length + ciphertext.byteLength
    )
    combined.set(salt, 0)
    combined.set(iv, salt.length)
    combined.set(new Uint8Array(ciphertext), salt.length + iv.length)

    return btoa(String.fromCharCode(...combined))
  }

  private extractEncryptionData(combined: string): {
    salt: Uint8Array
    iv: Uint8Array
    ciphertext: Uint8Array
  } {
    const data = new Uint8Array(
      atob(combined)
        .split('')
        .map(c => c.charCodeAt(0))
    )

    const salt = data.slice(0, AuthenticatorCrypto.SALT_LENGTH)
    const iv = data.slice(
      AuthenticatorCrypto.SALT_LENGTH,
      AuthenticatorCrypto.SALT_LENGTH + AuthenticatorCrypto.IV_LENGTH
    )
    const ciphertext = data.slice(
      AuthenticatorCrypto.SALT_LENGTH + AuthenticatorCrypto.IV_LENGTH
    )

    return { salt, iv, ciphertext }
  }

  validatePassword(password: string): boolean {
    return typeof password === 'string' && password.length > 0
  }

  isSecurePassword(password: string): boolean {
    if (!this.validatePassword(password)) {
      return false
    }

    return password.length >= 8
  }
}

export { AuthenticatorCrypto }
